/**
 * topicPalette — the single source of truth for topic identity color.
 *
 * The Charted Atlas design gives every discovery topic a fixed identity hue:
 * the "Jewel · classic" spectral wheel laid over the canonical dial order at
 * rotation 0 — Personality opens on the warm oranges at 12 o'clock and the
 * spectrum runs clockwise from there. The same hue paints the topic's
 * wedge on the polar chart, its dot on the Progress-tab rows, and its wedge on
 * the chat memory-update card; the marketing landing chart carries a build-time
 * copy of these values (see the cross-reference comment in
 * frontend-landing/src/pages/index.astro).
 *
 * A topic's color never varies with progress: wedges, dots and cores are the
 * full identity hue at any depth — depth is encoded by wedge RADIUS alone.
 * (The landing chart still carries build-time white-tinted copies of the
 * hues; that is a marketing composition, not this palette.)
 *
 * @module components/topicPalette
 */

import { DEPTH_BANDS } from './depthProgress.js';

/**
 * Frozen topic-id → identity hex. Jewel-classic spectral sequence at rotation 0
 * over the registry's dial order (the wheel verbatim, hue-sorted) — the
 * approved placement; treat as spec, not as a derivable value.
 * @type {Readonly<Object<string, string>>}
 */
export const TOPIC_COLORS = Object.freeze({
  'personality': '#d88969',
  'sense-of-self': '#d1793f',
  'inner-life': '#c1af3b',
  'connection': '#067a52',
  'communication-conflict': '#4fb493',
  'sex': '#56b1dc',
  'trust-repair': '#115f91',
  'values-daily-life': '#53459b',
  'money': '#723390',
  'family-parenting': '#d18dc2',
  'work-life-stress': '#853559',
  'grow-play': '#8c2f4a'
});

/** Resting fill of an uncharted ("mist") territory on white surfaces — the
 * Charted Atlas studio's haze, tuned one step lighter by Doug. (The
 * marketing chart uses its own near-white #fafafa instead — it sits on a
 * tinted page and must read white; see index.astro's COLOR CONTRACT note.) */
export const MIST_FILL = '#eff2f5';
/** Thickened mist tone — the mini map's gather-phase bump (the studio's raw
 * haze value). The full chart's spotlight no longer darkens the hovered
 * slice's mist; it fades every other slice instead. */
export const MIST_FILL_HOT = '#dfe7ee';

/**
 * The one fill for anything carrying a topic's identity — chart wedge, row
 * dot, memory-card core: the topic's full hue, regardless of progress.
 * Unknown topic ids return the neutral not-started gray rather than throwing.
 * @param {string} topicId - Registry topic id
 * @returns {string} Hex fill
 */
export function topicFill(topicId) {
  return TOPIC_COLORS[topicId] || DEPTH_BANDS.none;
}
